import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardMedia,
  Modal,
  IconButton,
  useTheme,
  keyframes,
  Fade
} from '@mui/material';
import { Close } from '@mui/icons-material';

// Keyframe animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

interface ImageData {
  id: string;
  src: string;
  alt: string;
  title: string;
}

const GalleryPage: React.FC = () => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);

  // Image data for img1-img5
  const images: ImageData[] = [
    {
      id: 'img1',
      src: '/image/img5.png',
      alt: 'Bild 1',
      title: 'Bild 1'
    },
    {
      id: 'img2',
      src: '/image/img2.png',
      alt: 'Bild 2',
      title: 'Bild 2'
    },
    {
      id: 'img3',
      src: '/image/img3.png',
      alt: 'Bild 3',
      title: 'Bild 3'
    },
    {
      id: 'img4',
      src: '/image/img4.png',
      alt: 'Bild 4',
      title: 'Bild 4'
    },
    {
      id: 'img5',
      src: '/image/img1.png',
      alt: 'Bild 5',
      title: 'Bild 5'
    }
  ];

  const handleImageClick = (image: ImageData) => {
    setSelectedImage(image);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  return (
    <Box
      sx={{
        minHeight: '80vh',
        py: 8,
        background: `radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}08 0%, transparent 50%),
                     radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}06 0%, transparent 50%)`,
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
            animation: `${slideInUp} 0.8s ease-out`,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Bildergalerie
          </Typography>
        </Box>

        {/* Image List - Single Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {images.map((image, index) => (
            <Card
              key={image.id}
              sx={{
                maxWidth: '800px',
                mx: 'auto',
                width: '100%',
                borderRadius: '20px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                background: `linear-gradient(135deg, ${theme.palette.background.paper}98 0%, ${theme.palette.background.paper}90 40%, ${theme.palette.primary.main}12 75%, ${theme.palette.primary.main}08 100%)`,
                backdropFilter: 'blur(25px)',
                border: `1px solid ${theme.palette.primary.main}25`,
                boxShadow: `0 12px 40px ${theme.palette.primary.main}20`,
                animation: `${scaleIn} 0.8s ease-out ${index * 0.15}s both`,
                '&:hover': {
                  transform: 'translateY(-12px) scale(1.01)',
                  boxShadow: `0 25px 70px ${theme.palette.primary.main}35`,
                  '& .image-container': {
                    transform: 'scale(1.02)',
                  },

                  '& .image-title': {
                    color: theme.palette.primary.main,
                  },
                },
              }}
              onClick={() => handleImageClick(image)}
            >
              {/* Image Header */}
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography
                  className="image-title"
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    color: 'text.primary',
                    transition: 'color 0.3s ease',
                    mb: 1,
                  }}
                >
                  {image.title}
                </Typography>
                <Box
                  sx={{
                    width: 60,
                    height: 4,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    borderRadius: '2px',
                    mx: 'auto',
                  }}
                />
              </Box>

              {/* Image Container */}
              <Box 
                className="image-container"
                sx={{ 
                  position: 'relative',
                  transition: 'transform 0.4s ease',
                  overflow: 'hidden',
                }}
              >
                <CardMedia
                  component="img"
                  height="400"
                  image={image.src}
                  alt={image.alt}
                  sx={{
                    objectFit: 'contain',
                    transition: 'all 0.4s ease',
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPgogIDx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIwLjNlbSI+QmlsZCBuaWNodCBnZWZ1bmRlbjwvdGV4dD4KPC9zdmc+';
                  }}
                />
                
                {/* Gradient Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '100px',
                    background: `linear-gradient(transparent, ${theme.palette.background.paper}20)`,
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            </Card>
          ))}
        </Box>
      </Container>

      {/* Modal for enlarged image */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          cursor: 'pointer',
        }}
        onClick={handleCloseModal}
      >
        <Fade in={!!selectedImage}>
          <Box
            sx={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedImage && (
              <>
                <Box
                  component="img"
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  onClick={handleCloseModal}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '95vh',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(0.98)',
                    },
                  }}
                />
                
                {/* Close Button */}
                <IconButton
                  onClick={handleCloseModal}
                  sx={{
                    position: 'fixed',
                    top: 20,
                    right: 20,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: 50,
                    height: 50,
                    zIndex: 1301,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Close fontSize="large" />
                </IconButton>
                
                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    position: 'fixed',
                    bottom: 30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(10px)',
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    fontWeight: 600,
                    zIndex: 1301,
                  }}
                >
                  {selectedImage.title}
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default GalleryPage;